
import React, { memo } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Star } from "lucide-react";

const TestimonialsSection = memo(() => {
  const testimonials = [
    {
      name: "Sarah Mitchell",
      location: "Hartford, CT",
      rating: 5,
      text: "Routine Cleaners has been a game-changer for our busy family! Their team is incredibly thorough and professional. Our house has never looked better, and the staff is always punctual and respectful.",
      initials: "SM"
    },
    {
      name: "Maria Rodriguez",
      location: "New Haven, CT",
      rating: 5,
      text: "As a business owner, I needed reliable office cleaning that wouldn't disrupt our operations. Routine Cleaners delivers consistently excellent service. They work around our schedule perfectly.",
      initials: "MR"
    },
    {
      name: "Jennifer Park",
      location: "Stamford, CT",
      rating: 5,
      text: "I was skeptical about hiring a cleaning service, but Routine Cleaners exceeded all my expectations. They pay attention to every detail and use eco-friendly products. Highly recommend!",
      initials: "JP"
    },
    {
      name: "Diana Thompson",
      location: "Bridgeport, CT",
      rating: 5,
      text: "After our kitchen renovation, we needed post-construction cleaning. Routine Cleaners made our home spotless and move-in ready. Their attention to detail is remarkable!",
      initials: "DT"
    },
    {
      name: "Lisa Chen",
      location: "Waterbury, CT",
      rating: 5,
      text: "I run several Airbnb properties and Routine Cleaners keeps them guest-ready with quick turnarounds. My guests consistently mention how clean the spaces are. Five stars!",
      initials: "LC"
    },
    {
      name: "Rebecca Johnson",
      location: "Norwalk, CT",
      rating: 5,
      text: "Routine Cleaners' deep cleaning service transformed our home. The team was professional, efficient, and left no corner untouched. Worth every penny for the peace of mind.",
      initials: "RJ"
    }
  ];

  return (
    <section className="py-20 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            What Our <span className="bg-gradient-to-r from-brand-gold-dark to-brand-gold-light bg-clip-text text-transparent">Customers Say</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Don't just take our word for it. Here's what our satisfied customers 
            across Connecticut have to say about our cleaning services.
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-border">
              <CardContent className="p-6">
                {/* Stars */}
                <div className="flex space-x-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-brand-gold-dark text-brand-gold-dark" />
                  ))}
                </div>
                
                {/* Testimonial Text */}
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  "{testimonial.text}"
                </p>
                
                {/* Customer Info */}
                <div className="flex items-center space-x-3">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-accent text-accent-foreground font-semibold">
                      {testimonial.initials}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="font-semibold text-foreground">{testimonial.name}</div>
                    <div className="text-sm text-muted-foreground">{testimonial.location}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <div className="bg-muted rounded-2xl p-8 lg:p-12">
            <h3 className="text-2xl lg:text-3xl font-bold text-foreground mb-4">
              Ready to Join Our Satisfied Customers?
            </h3>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Experience the same level of exceptional service that has earned us 
              5-star reviews across Connecticut.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <div className="flex items-center justify-center space-x-2 text-sm text-muted-foreground">
                <Star className="h-4 w-4 fill-brand-gold-dark text-brand-gold-dark" />
                <span>5.0 Star Rating</span>
              </div>
              <div className="flex items-center justify-center space-x-2 text-sm text-muted-foreground">
                <div className="w-2 h-2 bg-accent rounded-full"></div>
                <span>500+ Happy Customers</span>
              </div>
              <div className="flex items-center justify-center space-x-2 text-sm text-muted-foreground">
                <div className="w-2 h-2 bg-accent rounded-full"></div>
                <span>100% Satisfaction Guarantee</span>
              </div>
            </div>
            <Button variant="hero" size="lg" asChild>
              <a href="/about">Learn More About Us</a>
            </Button>
          </div>
        </div>

        {/* Referral Program Banner */}
        <div className="mt-8 max-w-3xl mx-auto">
          <div className="bg-gradient-to-r from-accent/10 to-accent/5 border border-accent/20 rounded-xl p-6 text-center">
            <p className="text-lg text-foreground mb-2">
              <span className="font-semibold">Know someone who needs a clean home?</span>
            </p>
            <p className="text-muted-foreground mb-4">
              Refer them and earn rewards! Get 20% off or $30 cash for each referral.
            </p>
            <Button variant="outline" asChild>
              <a href="/referral">Learn About Our Referral Program</a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
});

TestimonialsSection.displayName = 'TestimonialsSection';

export default TestimonialsSection;
